import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { AdminBatchesContent } from "@/pages/AdminBatchesPage";
import CourseBuilder from "@/components/admin/CourseBuilder";
import { CourseEditForm } from "@/pages/AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Layers, BookOpen, Video, FileText, Plus, Trash2, PenSquare,
  Upload, ExternalLink, Calendar, Clock, RefreshCw, CheckCircle2,
  AlertCircle, Shield, Link2, Download
} from "lucide-react";
import { toast } from "sonner";

export default function ManagementDashboard() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [activeTab, setActiveTab] = useState("batches");
  const [courses, setCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Course Management State
  const [editingCourse, setEditingCourse] = useState(null); // null, "new", or "content-<courseId>"

  // Live Class Scheduling State
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [schedulingClass, setSchedulingClass] = useState(false);
  const [classForm, setClassForm] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    duration: 60,
    meeting_link: "",
    source: "zoom",
    course_id: "all"
  });

  // Recording & Notes Upload Modal State
  const [uploadModal, setUploadModal] = useState(null); // 'recording' | 'note' | null
  const [uploading, setUploading] = useState(false);
  const [newRecording, setNewRecording] = useState({
    title: "",
    description: "",
    video_url: "",
    course_id: "all",
    duration_minutes: ""
  });
  const [recordingFile, setRecordingFile] = useState(null);

  const [newNote, setNewNote] = useState({
    title: "",
    description: "",
    file_url: "",
    course_id: "all"
  });
  const [noteFile, setNoteFile] = useState(null);

  // Fetch all management data
  const fetchData = useCallback(async () => {
    try {
      const [coursesRes, classesRes, recsRes, notesRes] = await Promise.allSettled([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/live-classes`, { withCredentials: true }),
        axios.get(`${API}/recordings`, { withCredentials: true }),
        axios.get(`${API}/notes`, { withCredentials: true })
      ]);

      if (coursesRes.status === "fulfilled") {
        const raw = coursesRes.value.data;
        setCourses(Array.isArray(raw) ? raw : raw?.courses || []);
      }
      if (classesRes.status === "fulfilled") {
        const raw = classesRes.value.data;
        setLiveClasses(Array.isArray(raw) ? raw : raw?.classes || []);
      }
      if (recsRes.status === "fulfilled") {
        const raw = recsRes.value.data;
        setRecordings(Array.isArray(raw) ? raw : raw?.recordings || []);
      }
      if (notesRes.status === "fulfilled") {
        const raw = notesRes.value.data;
        setNotes(Array.isArray(raw) ? raw : raw?.notes || []);
      }
    } catch (e) {
      console.error("Management fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // File Upload Helper
  const uploadFile = async (file, kind) => {
    const data = new FormData();
    data.append("file", file);
    data.append("kind", kind);
    const res = await axios.post(`${API}/uploads`, data, { withCredentials: true });
    return res.data.url;
  };

  // Course Actions
  const handleSaveCourse = async (courseId, data) => {
    try {
      if (courseId === "new") {
        await axios.post(`${API}/courses`, data, { withCredentials: true });
        toast.success("Course created successfully!");
      } else {
        await axios.put(`${API}/courses/${courseId}`, data, { withCredentials: true });
        toast.success("Course updated successfully!");
      }
      setEditingCourse(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save course");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course along with all its modules and lessons?")) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
      toast.success("Course deleted");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete course");
    }
  };

  // Live Class Actions
  const handleScheduleClass = async (e) => {
    e.preventDefault();
    if (!classForm.title || !classForm.scheduled_at) {
      toast.error("Please enter a title and scheduled date/time");
      return;
    }
    setSchedulingClass(true);
    try {
      const payload = {
        title: classForm.title.trim(),
        description: classForm.description.trim(),
        scheduled_at: classForm.scheduled_at,
        duration: parseInt(classForm.duration, 10) || 60,
        meeting_link: classForm.meeting_link.trim(),
        source: classForm.source,
        course_id: classForm.course_id === "all" ? null : classForm.course_id
      };
      await axios.post(`${API}/live-classes`, payload, { withCredentials: true });
      toast.success("Live class scheduled successfully!");
      setIsScheduleOpen(false);
      setClassForm({
        title: "",
        description: "",
        scheduled_at: "",
        duration: 60,
        meeting_link: "",
        source: "manual",
        course_id: "all"
      });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to schedule live class");
    } finally {
      setSchedulingClass(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm("Are you sure you want to delete this live class?")) return;
    try {
      await axios.delete(`${API}/live-classes/${classId}`, { withCredentials: true });
      toast.success("Live class removed");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete class");
    }
  };

  // Recording Actions
  const handleUploadRecording = async () => {
    if (!newRecording.title || (!newRecording.video_url && !recordingFile)) {
      toast.error("Please enter a title and provide a video URL or upload a video file");
      return;
    }
    setUploading(true);
    try {
      const videoUrl = recordingFile
        ? await uploadFile(recordingFile, "recording")
        : newRecording.video_url;

      await axios.post(
        `${API}/recordings`,
        {
          title: newRecording.title.trim(),
          description: newRecording.description || "",
          video_url: videoUrl,
          course_id: newRecording.course_id === "all" ? null : (newRecording.course_id || null),
          duration_minutes: parseInt(newRecording.duration_minutes, 10) || null,
        },
        { withCredentials: true }
      );
      toast.success("Recording uploaded successfully!");
      setNewRecording({ title: "", description: "", video_url: "", course_id: "all", duration_minutes: "" });
      setRecordingFile(null);
      setUploadModal(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload recording");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteRecording = async (recId) => {
    if (!window.confirm("Are you sure you want to delete this recording?")) return;
    try {
      await axios.delete(`${API}/recordings/${recId}`, { withCredentials: true });
      toast.success("Recording deleted");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete recording");
    }
  };

  // Notes Actions
  const handleUploadNote = async () => {
    if (!newNote.title || (!newNote.file_url && !noteFile)) {
      toast.error("Please enter a title and upload a file or paste a link");
      return;
    }
    setUploading(true);
    try {
      const fileUrl = noteFile
        ? await uploadFile(noteFile, "note")
        : newNote.file_url;

      await axios.post(
        `${API}/notes`,
        {
          title: newNote.title.trim(),
          description: newNote.description || "",
          course_id: newNote.course_id === "all" ? null : (newNote.course_id || null),
          file_url: fileUrl,
        },
        { withCredentials: true }
      );
      toast.success("Note uploaded successfully!");
      setNewNote({ title: "", description: "", file_url: "", course_id: "all" });
      setNoteFile(null);
      setUploadModal(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload note");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await axios.delete(`${API}/notes/${noteId}`, { withCredentials: true });
      toast.success("Note deleted");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete note");
    }
  };

  return (
    <DashboardLayout title="Management Portal">
      <div className="space-y-6 max-w-7xl mx-auto pb-12" data-testid="management-dashboard">
        {/* Management Portal Banner */}
        <div className={`rounded-2xl p-6 border transition-all ${
          isLight
            ? "bg-gradient-to-r from-purple-50 via-indigo-50/50 to-white border-purple-200/80 shadow-xs"
            : "bg-gradient-to-r from-[#170B28] via-[#0E1326] to-[#0A0D18] border-purple-900/30 shadow-2xl"
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/70 dark:text-purple-300 dark:border-purple-800 text-xs font-semibold px-2.5 py-0.5 uppercase tracking-wide">
                  <Shield className="w-3 h-3 mr-1 inline" /> Management Role
                </Badge>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-plex">
                  Full control over Batches, Courses, Live Classes, Recordings & Notes
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-outfit font-extrabold text-gray-900 dark:text-white tracking-tight">
                Management Portal
              </h1>
              <p className="text-sm text-gray-600 dark:text-zinc-400 font-plex mt-1 max-w-2xl">
                Create & edit courses, schedule live sessions, manage student batches, and upload study materials and video recordings.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => {
                  setActiveTab("courses");
                  setEditingCourse("new");
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs rounded-xl shadow-xs h-9 px-3.5"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New Course
              </Button>
              <Button
                onClick={() => setIsScheduleOpen(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium text-xs rounded-xl shadow-xs h-9 px-3.5"
              >
                <Video className="w-4 h-4 mr-1.5" /> Schedule Class
              </Button>
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-purple-200/50 dark:border-purple-900/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex">Live Batches</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white font-outfit">Active</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex">Total Courses</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white font-outfit">{courses.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0">
                <Video className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex">Live Classes</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white font-outfit">{liveClasses.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex">Recordings & Notes</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white font-outfit">{recordings.length + notes.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
          <TabsList className={`p-1 rounded-xl h-auto flex flex-wrap border ${
            isLight ? "bg-gray-100 border-gray-200" : "bg-[#131B2C] border-white/[0.06]"
          }`}>
            <TabsTrigger
              value="batches"
              className="gap-2 px-4 py-2 rounded-lg text-xs font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-purple-600 data-[state=active]:text-purple-900 dark:data-[state=active]:text-white shadow-xs"
            >
              <Layers className="w-4 h-4" /> Live Batches
            </TabsTrigger>
            <TabsTrigger
              value="courses"
              className="gap-2 px-4 py-2 rounded-lg text-xs font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-emerald-600 data-[state=active]:text-emerald-900 dark:data-[state=active]:text-white shadow-xs"
            >
              <BookOpen className="w-4 h-4" /> Course Management
            </TabsTrigger>
            <TabsTrigger
              value="live-classes"
              className="gap-2 px-4 py-2 rounded-lg text-xs font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-cyan-600 data-[state=active]:text-cyan-900 dark:data-[state=active]:text-white shadow-xs"
            >
              <Video className="w-4 h-4" /> Live Classes
            </TabsTrigger>
            <TabsTrigger
              value="recordings-notes"
              className="gap-2 px-4 py-2 rounded-lg text-xs font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-amber-600 data-[state=active]:text-amber-900 dark:data-[state=active]:text-white shadow-xs"
            >
              <FileText className="w-4 h-4" /> Recordings & Notes
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Live Batches */}
          <TabsContent value="batches" className="mt-0 focus-visible:ring-0">
            <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.06]">
              <AdminBatchesContent embedded={true} />
            </div>
          </TabsContent>

          {/* TAB 2: Course Management */}
          <TabsContent value="courses" className="mt-0 focus-visible:ring-0">
            {editingCourse?.startsWith("content-") ? (
              <div className="animate-in fade-in duration-200">
                <CourseBuilder
                  courseId={editingCourse.replace("content-", "")}
                  onBack={() => {
                    setEditingCourse(null);
                    fetchData();
                  }}
                />
              </div>
            ) : (
              <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <div>
                    <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      Course Builder & Content
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Create structured curriculums, organize modules, and upload lessons
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setEditingCourse("new")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs h-9 rounded-xl px-4 text-xs"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Add Course
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editingCourse === "new" && (
                    <div className="rounded-xl p-5 border border-emerald-500/30 bg-emerald-50/20 dark:bg-emerald-950/20 mb-6">
                      <h3 className="text-gray-900 dark:text-white font-outfit font-bold mb-4">Create New Course</h3>
                      <CourseEditForm
                        course={{}}
                        onSave={(data) => handleSaveCourse("new", data)}
                        onCancel={() => setEditingCourse(null)}
                      />
                    </div>
                  )}

                  {courses.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-zinc-400">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-zinc-700" />
                      <p className="font-semibold text-sm">No courses found</p>
                      <p className="text-xs mt-1">Get started by creating your first course above.</p>
                    </div>
                  ) : (
                    courses.map((course) => (
                      <div
                        key={course.course_id}
                        className={`rounded-xl p-4 border transition-all ${
                          isLight ? "bg-gray-50/70 border-gray-200 hover:bg-gray-50" : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]"
                        }`}
                      >
                        {editingCourse === course.course_id ? (
                          <div className="animate-in fade-in duration-200">
                            <h3 className="text-gray-900 dark:text-white font-outfit font-bold mb-4">Edit Course Basic Details</h3>
                            <CourseEditForm
                              course={course}
                              onSave={(data) => handleSaveCourse(course.course_id, data)}
                              onCancel={() => setEditingCourse(null)}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                <h3 className="text-gray-900 dark:text-white font-outfit font-bold text-base truncate">
                                  {course.title}
                                </h3>
                                <Badge className={`${
                                  course.is_free
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                                    : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800"
                                } text-[11px] font-semibold`}>
                                  {course.is_free ? "FREE" : (course.price_display || `₹${course.price}`)}
                                </Badge>
                                {course.sessions && (
                                  <Badge variant="outline" className="text-[10px] text-gray-500">
                                    {course.sessions} sessions
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex line-clamp-2">
                                {course.description || "No description provided."}
                              </p>
                              {course.slug && (
                                <span className="text-[10px] text-cyan-600 dark:text-cyan-400 font-mono mt-1.5 inline-block">
                                  /courses/{course.slug}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                size="sm"
                                onClick={() => setEditingCourse(`content-${course.course_id}`)}
                                className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800 font-semibold h-8 rounded-lg text-xs"
                              >
                                <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Manage Content
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingCourse(course.course_id)}
                                className="h-8 rounded-lg text-xs font-semibold px-2.5 border-gray-200 dark:border-white/10"
                              >
                                <PenSquare className="w-3.5 h-3.5 mr-1" /> Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteCourse(course.course_id)}
                                className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 border-gray-200 dark:border-white/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TAB 3: Live Classes */}
          <TabsContent value="live-classes" className="mt-0 focus-visible:ring-0">
            <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg flex items-center gap-2">
                    <Video className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                    Live Class Schedules
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Schedule live trading classes via Zoom or paste streaming meeting links
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setIsScheduleOpen(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium shadow-xs h-9 rounded-xl px-4 text-xs"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Schedule Class
                </Button>
              </CardHeader>
              <CardContent>
                {liveClasses.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-zinc-400">
                    <Video className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-zinc-700" />
                    <p className="font-semibold text-sm">No live classes scheduled</p>
                    <p className="text-xs mt-1">Schedule your next interactive session using the button above.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {liveClasses.map((lc) => (
                      <div
                        key={lc.class_id}
                        className={`rounded-xl p-4 border flex flex-col justify-between ${
                          isLight ? "bg-gray-50/70 border-gray-200" : "bg-white/[0.02] border-white/[0.06]"
                        }`}
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="text-sm font-bold font-outfit text-gray-900 dark:text-white line-clamp-1">
                              {lc.title}
                            </h4>
                            <Badge className={`${
                              lc.status === "completed"
                                ? "bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-gray-400"
                                : lc.status === "live"
                                ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 animate-pulse"
                                : "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300"
                            } text-[10px] font-semibold uppercase`}>
                              {lc.status || "upcoming"}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex line-clamp-2 mb-3">
                            {lc.description || "No description provided."}
                          </p>
                          <div className="space-y-1 text-xs text-gray-500 dark:text-zinc-400 font-plex">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                              <span>{lc.scheduled_at ? new Date(lc.scheduled_at).toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Date TBD"}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                              <span>{lc.duration || 60} mins duration</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 pt-4 mt-3 border-t border-gray-200/60 dark:border-white/[0.06] flex-wrap">
                          <div className="flex items-center gap-2">
                            {lc.start_url && lc.status !== "completed" && (
                              <a
                                href={lc.start_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-bold text-amber-500 hover:text-amber-400 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Start as Host
                              </a>
                            )}
                            {(lc.join_url || lc.meeting_link) ? (
                              <a
                                href={lc.join_url || lc.meeting_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Launch Meeting
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">No meeting URL</span>
                            )}
                          </div>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClass(lc.class_id)}
                            className="h-7 px-2 text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Recordings & Notes */}
          <TabsContent value="recordings-notes" className="mt-0 focus-visible:ring-0 space-y-6">
            {/* Recordings Section */}
            <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg flex items-center gap-2">
                    <Video className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Video Recordings
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Upload recorded sessions or paste YouTube, Vimeo, Drive, or Zoom links
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setUploadModal("recording")}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-xs h-9 rounded-xl px-4 text-xs"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> Upload Recording
                </Button>
              </CardHeader>
              <CardContent>
                {recordings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-zinc-400">
                    <p className="text-xs">No video recordings uploaded yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {recordings.map((rec) => (
                      <div
                        key={rec.recording_id}
                        className={`rounded-xl p-3.5 border flex flex-col justify-between ${
                          isLight ? "bg-gray-50/70 border-gray-200" : "bg-white/[0.02] border-white/[0.06]"
                        }`}
                      >
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1 mb-1 font-outfit">
                            {rec.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex line-clamp-2 mb-2">
                            {rec.description || "No description."}
                          </p>
                          {rec.duration_minutes && (
                            <span className="text-[10px] text-gray-400 font-mono">
                              {rec.duration_minutes} mins
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200/50 dark:border-white/[0.04]">
                          {rec.video_url && (
                            <a
                              href={rec.video_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-semibold"
                            >
                              <ExternalLink className="w-3 h-3" /> View Video
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRecording(rec.recording_id)}
                            className="h-7 px-2 text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes & Materials Section */}
            <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-gray-900 dark:text-white font-outfit text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    Study Notes & PDFs
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Upload presentation slides, trade notes, and cheat sheets for students
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setUploadModal("note")}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-xs h-9 rounded-xl px-4 text-xs"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> Upload Note / PDF
                </Button>
              </CardHeader>
              <CardContent>
                {notes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-zinc-400">
                    <p className="text-xs">No notes or PDF resources uploaded yet.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {notes.map((note) => (
                      <div
                        key={note.note_id}
                        className={`rounded-xl p-3.5 border flex flex-col justify-between ${
                          isLight ? "bg-gray-50/70 border-gray-200" : "bg-white/[0.02] border-white/[0.06]"
                        }`}
                      >
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1 mb-1 font-outfit">
                            {note.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex line-clamp-2 mb-2">
                            {note.description || "No description."}
                          </p>
                        </div>
                        <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200/50 dark:border-white/[0.04]">
                          {note.file_url && (
                            <a
                              href={note.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1 font-semibold"
                            >
                              <Download className="w-3 h-3" /> Download PDF
                            </a>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteNote(note.note_id)}
                            className="h-7 px-2 text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Schedule Live Class Dialog */}
        <Dialog open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
          <DialogContent className="max-w-md bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/10">
            <DialogHeader>
              <DialogTitle className="text-lg font-outfit font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-cyan-600 dark:text-cyan-400" /> Schedule Live Class
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-zinc-400">
                Create a class for all students or link it to a specific course.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleScheduleClass} className="space-y-3.5 pt-2">
              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Class Title *</Label>
                <Input
                  required
                  placeholder="e.g., Live Price Action Analysis & Strategy"
                  value={classForm.title}
                  onChange={(e) => setClassForm((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Description</Label>
                <Textarea
                  placeholder="What will be covered in this session?"
                  value={classForm.description}
                  onChange={(e) => setClassForm((p) => ({ ...p, description: e.target.value }))}
                  className="mt-1 min-h-[60px] text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600 dark:text-zinc-300">Date & Time *</Label>
                  <Input
                    required
                    type="datetime-local"
                    value={classForm.scheduled_at}
                    onChange={(e) => setClassForm((p) => ({ ...p, scheduled_at: e.target.value }))}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 dark:text-zinc-300">Duration (Minutes)</Label>
                  <Input
                    type="number"
                    min="15"
                    max="300"
                    value={classForm.duration}
                    onChange={(e) => setClassForm((p) => ({ ...p, duration: e.target.value }))}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Link to Course</Label>
                <Select
                  value={classForm.course_id}
                  onValueChange={(val) => setClassForm((p) => ({ ...p, course_id: val }))}
                >
                  <SelectTrigger className="mt-1 h-9 text-xs">
                    <SelectValue placeholder="All Students" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students (General Session)</SelectItem>
                    {courses.map((c) => (
                      <SelectItem key={c.course_id} value={c.course_id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Meeting Source</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Button
                    type="button"
                    variant={classForm.source === "zoom" ? "default" : "outline"}
                    onClick={() => setClassForm((p) => ({ ...p, source: "zoom" }))}
                    className={`h-9 text-xs rounded-lg font-semibold ${
                      classForm.source === "zoom" ? "bg-cyan-600 text-white" : "border-gray-200 dark:border-white/10"
                    }`}
                  >
                    <Video className="w-3.5 h-3.5 mr-1.5" /> Automated Zoom
                  </Button>
                  <Button
                    type="button"
                    variant={classForm.source === "manual" ? "default" : "outline"}
                    onClick={() => setClassForm((p) => ({ ...p, source: "manual" }))}
                    className={`h-9 text-xs rounded-lg font-semibold ${
                      classForm.source === "manual" ? "bg-cyan-600 text-white" : "border-gray-200 dark:border-white/10"
                    }`}
                  >
                    <Link2 className="w-3.5 h-3.5 mr-1.5" /> Paste Link
                  </Button>
                </div>
              </div>

              {classForm.source === "manual" ? (
                <div>
                  <Label className="text-xs text-gray-600 dark:text-zinc-300">Meeting Link * (Zoom / Meet / YouTube)</Label>
                  <Input
                    required
                    placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                    value={classForm.meeting_link}
                    onChange={(e) => setClassForm((p) => ({ ...p, meeting_link: e.target.value }))}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-cyan-50/50 dark:bg-cyan-950/20 border border-cyan-200/60 dark:border-cyan-800/40 text-xs text-cyan-800 dark:text-cyan-300">
                  <p className="font-semibold flex items-center gap-1.5">
                    <Video className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /> Automated Zoom Meeting
                  </p>
                  <p className="text-[11px] mt-1 text-cyan-700/80 dark:text-cyan-300/80">
                    Zoom link will be generated automatically in Indian Standard Time (Asia/Kolkata).
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-white/10">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsScheduleOpen(false)}
                  className="h-8 text-xs rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={schedulingClass}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium h-8 text-xs rounded-lg px-4"
                >
                  {schedulingClass ? "Scheduling..." : "Schedule Class"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Upload Recording Dialog */}
        <Dialog open={uploadModal === "recording"} onOpenChange={(open) => !open && setUploadModal(null)}>
          <DialogContent className="max-w-md bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/10">
            <DialogHeader>
              <DialogTitle className="text-lg font-outfit font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Upload Recording
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-zinc-400">
                Upload a direct video or paste an external video URL.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 pt-2">
              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Recording Title *</Label>
                <Input
                  placeholder="e.g., Session 4: Advanced Fibonacci Retracement"
                  value={newRecording.title}
                  onChange={(e) => setNewRecording((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Description</Label>
                <Textarea
                  placeholder="Summary of this recording"
                  value={newRecording.description}
                  onChange={(e) => setNewRecording((p) => ({ ...p, description: e.target.value }))}
                  className="mt-1 min-h-[60px] text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600 dark:text-zinc-300">Course</Label>
                  <Select
                    value={newRecording.course_id}
                    onValueChange={(val) => setNewRecording((p) => ({ ...p, course_id: val }))}
                  >
                    <SelectTrigger className="mt-1 h-9 text-xs">
                      <SelectValue placeholder="All Students" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Students</SelectItem>
                      {courses.map((c) => (
                        <SelectItem key={c.course_id} value={c.course_id}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 dark:text-zinc-300">Duration (Minutes)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 75"
                    value={newRecording.duration_minutes}
                    onChange={(e) => setNewRecording((p) => ({ ...p, duration_minutes: e.target.value }))}
                    className="mt-1 h-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Video Link (YouTube / Drive / Vimeo)</Label>
                <Input
                  placeholder="https://..."
                  value={newRecording.video_url}
                  onChange={(e) => setNewRecording((p) => ({ ...p, video_url: e.target.value }))}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Or Upload Video File (&lt; 100MB)</Label>
                <Input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setRecordingFile(e.target.files?.[0] || null)}
                  className="mt-1 h-9 text-xs cursor-pointer"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-white/10">
                <Button
                  variant="ghost"
                  onClick={() => setUploadModal(null)}
                  className="h-8 text-xs rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadRecording}
                  disabled={uploading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium h-8 text-xs rounded-lg px-4"
                >
                  {uploading ? "Uploading..." : "Save Recording"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Note Dialog */}
        <Dialog open={uploadModal === "note"} onOpenChange={(open) => !open && setUploadModal(null)}>
          <DialogContent className="max-w-md bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/10">
            <DialogHeader>
              <DialogTitle className="text-lg font-outfit font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" /> Upload Study Material
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-zinc-400">
                Upload a PDF document or paste a resource link.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 pt-2">
              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Note Title *</Label>
                <Input
                  placeholder="e.g., Candlestick Cheat Sheet PDF"
                  value={newNote.title}
                  onChange={(e) => setNewNote((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Description</Label>
                <Textarea
                  placeholder="Overview of this study resource"
                  value={newNote.description}
                  onChange={(e) => setNewNote((p) => ({ ...p, description: e.target.value }))}
                  className="mt-1 min-h-[60px] text-xs"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Course</Label>
                <Select
                  value={newNote.course_id}
                  onValueChange={(val) => setNewNote((p) => ({ ...p, course_id: val }))}
                >
                  <SelectTrigger className="mt-1 h-9 text-xs">
                    <SelectValue placeholder="All Students" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Students</SelectItem>
                    {courses.map((c) => (
                      <SelectItem key={c.course_id} value={c.course_id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Upload PDF / Document</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.png,.jpg"
                  onChange={(e) => setNoteFile(e.target.files?.[0] || null)}
                  className="mt-1 h-9 text-xs cursor-pointer"
                />
              </div>

              <div>
                <Label className="text-xs text-gray-600 dark:text-zinc-300">Or Paste Document Link</Label>
                <Input
                  placeholder="https://drive.google.com/..."
                  value={newNote.file_url}
                  onChange={(e) => setNewNote((p) => ({ ...p, file_url: e.target.value }))}
                  className="mt-1 h-9 text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-white/10">
                <Button
                  variant="ghost"
                  onClick={() => setUploadModal(null)}
                  className="h-8 text-xs rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadNote}
                  disabled={uploading}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-medium h-8 text-xs rounded-lg px-4"
                >
                  {uploading ? "Uploading..." : "Save Material"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
